import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus,
  FiSearch,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiX,
  FiEye,
  FiEdit2,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import ScheduleInterview from './ScheduleInterview';
import {
  INTERVIEWS_QUERY,
  UPDATE_INTERVIEW,
  UPDATE_INTERVIEW_STATUS,
  INTERVIEW_TYPE_LABEL,
  INTERVIEW_STATUS_LABEL,
  applicantInitials,
  mapInterview,
} from '../data/recruitmentApi';
import '../styles/recruitmentShared.css';
import '../styles/AddApplicant.css';
import '../styles/InterviewManagement.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function buildMonthCells(anchor) {
  const first = startOfMonth(anchor);
  const startPad = first.getDay();
  const daysInMonth = new Date(
    anchor.getFullYear(),
    anchor.getMonth() + 1,
    0,
  ).getDate();
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(anchor.getFullYear(), anchor.getMonth(), day));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function InterviewManagement() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [interviews, setInterviews] = useState([]);
  const overlay = useAdminListOverlay(interviews, {
    allowedTabs: ['schedule', 'calendar'],
    extraClearKeys: ['schedule'],
  });
  const view = overlay.tab === 'calendar' ? 'calendar' : 'schedule';
  const scheduleId = overlay.get('schedule');
  const showSchedule = overlay.showAdd || Boolean(scheduleId);
  const preselectedApplicantId = scheduleId;
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => new Date());
  const activeInterview = overlay.editing || overlay.viewing;
  const [editForm, setEditForm] = useState({
    interviewer: '',
    type: 'video',
    scheduledAt: '',
    location: '',
    notes: '',
    status: 'scheduled',
  });
  const [savingEdit, setSavingEdit] = useState(false);

  const fetchInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const data = await graphqlRequest(INTERVIEWS_QUERY);
      const rows = Array.isArray(data?.recruitmentInterviews)
        ? data.recruitmentInterviews.map(mapInterview)
        : [];
      setInterviews(rows);
    } catch (err) {
      console.error(err);
      setInterviews([]);
      toast.error(err?.message || 'Failed to load interviews.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchInterviews();
  }, [fetchInterviews]);

  const stats = useMemo(() => {
    const today = new Date();
    return [
      {
        id: 'today',
        label: 'Scheduled Today',
        value: interviews.filter((i) => {
          if (!i.scheduledAt) return false;
          const d = new Date(i.scheduledAt);
          return (
            !Number.isNaN(d.getTime()) &&
            sameDay(d, today) &&
            (i.status === 'scheduled' || i.status === 'rescheduled')
          );
        }).length,
        icon: FiCalendar,
        tone: 'blue',
      },
      {
        id: 'completed',
        label: 'Completed',
        value: interviews.filter((i) => i.status === 'completed').length,
        icon: FiCheckCircle,
        tone: 'green',
      },
      {
        id: 'pending',
        label: 'Upcoming',
        value: interviews.filter(
          (i) => i.status === 'scheduled' || i.status === 'rescheduled',
        ).length,
        icon: FiClock,
        tone: 'yellow',
      },
      {
        id: 'cancelled',
        label: 'Cancelled',
        value: interviews.filter((i) => i.status === 'cancelled').length,
        icon: FiXCircle,
        tone: 'red',
      },
    ];
  }, [interviews]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return interviews;
    return interviews.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        i.role.toLowerCase().includes(q) ||
        String(i.interviewer || '')
          .toLowerCase()
          .includes(q) ||
        String(i.location || '')
          .toLowerCase()
          .includes(q),
    );
  }, [interviews, search]);

  const monthLabel = useMemo(
    () =>
      monthAnchor.toLocaleDateString('en-AU', {
        month: 'long',
        year: 'numeric',
      }),
    [monthAnchor],
  );

  const monthCells = useMemo(() => buildMonthCells(monthAnchor), [monthAnchor]);

  const interviewsByDayKey = useMemo(() => {
    const map = new Map();
    filtered.forEach((row) => {
      if (!row.scheduledAt) return;
      const d = new Date(row.scheduledAt);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });
    return map;
  }, [filtered]);

  const dayKey = (d) =>
    d ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : '';

  const selectedDayInterviews = useMemo(() => {
    if (!selectedDay) return [];
    return interviewsByDayKey.get(dayKey(selectedDay)) || [];
  }, [interviewsByDayKey, selectedDay]);

  const goRecruitment = () => {
    navigate({ pathname: '/', search: '?page=recruitment' }, { replace: true });
  };

  const openSchedule = (applicantId = '') => {
    if (applicantId) {
      overlay.patch({ schedule: applicantId }, ['view', 'edit', 'action']);
    } else {
      overlay.patch({ action: 'add' }, ['view', 'edit', 'schedule']);
    }
  };

  const toLocalInputValue = (value) => {
    const d = value ? new Date(value) : new Date();
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openView = (row) => overlay.openView(row);

  const formFromInterview = (row) => ({
    interviewer: row.interviewer === 'Unassigned' ? '' : row.interviewer || '',
    type: row.type || 'video',
    scheduledAt: toLocalInputValue(row.scheduledAt),
    location: row.location === 'TBD' ? '' : row.location || '',
    notes: row.notes || '',
    status: row.status || 'scheduled',
  });

  const openEdit = (row) => overlay.openEdit(row);

  useEffect(() => {
    if (!overlay.editing) return;
    setEditForm(formFromInterview(overlay.editing));
  }, [overlay.editing]);

  const closeDetail = () => overlay.closeOverlay();

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!activeInterview?.id) return;
    if (!editForm.scheduledAt) {
      toast.error('Please choose date and time');
      return;
    }
    setSavingEdit(true);
    try {
      await graphqlRequest(UPDATE_INTERVIEW, {
        id: activeInterview.id,
        input: {
          interviewer: editForm.interviewer.trim() || null,
          type: editForm.type,
          scheduledAt: new Date(editForm.scheduledAt).toISOString(),
          location: editForm.location.trim() || null,
          notes: editForm.notes.trim() || null,
          status: editForm.status,
        },
      });
      toast.success('Interview updated.');
      closeDetail();
      await fetchInterviews();
    } catch (err) {
      toast.error(err?.message || 'Could not update interview.');
    } finally {
      setSavingEdit(false);
    }
  };

  const markCompleted = async (id) => {
    setUpdatingId(id);
    try {
      await graphqlRequest(UPDATE_INTERVIEW_STATUS, {
        id,
        status: 'completed',
      });
      toast.success('Interview completed. Candidate moved to Offer.');
      await fetchInterviews();
    } catch (err) {
      toast.error(err?.message || 'Could not update interview.');
    } finally {
      setUpdatingId(null);
    }
  };

  const markCancelled = async (id) => {
    setUpdatingId(id);
    try {
      await graphqlRequest(UPDATE_INTERVIEW_STATUS, {
        id,
        status: 'cancelled',
      });
      toast.success('Interview cancelled.');
      await fetchInterviews();
    } catch (err) {
      toast.error(err?.message || 'Could not cancel interview.');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="rm-shared ri-page">
      <nav className="rm-crumb" aria-label="Breadcrumb">
        <button type="button" className="rm-crumb-back" onClick={goRecruitment}>
          <FiChevronLeft />
          Recruitment
        </button>
        <span className="rm-crumb-sep">/</span>
        <span>Interviews</span>
      </nav>

      <header className="rm-page-header">
        <div>
          <h1 className="rm-page-title">Interview Management</h1>
          <p className="rm-page-subtitle">
            Schedule, track, and manage all candidate interviews.
          </p>
        </div>
        <div className="rm-actions">
          <button
            type="button"
            className="rm-btn rm-btn--primary"
            onClick={() => openSchedule()}
          >
            <FiPlus />
            Schedule Interview
          </button>
        </div>
      </header>

      <section
        className="rm-stat-grid rm-stat-grid--4"
        aria-label="Interview statistics"
      >
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <article key={stat.id} className="rm-stat-card">
              <div className={`rm-stat-icon rm-stat-icon--${stat.tone}`}>
                <Icon />
              </div>
              <div>
                <div className="rm-stat-value">{stat.value}</div>
                <div className="rm-stat-label">{stat.label}</div>
              </div>
            </article>
          );
        })}
      </section>

      <div className="ri-toolbar">
        <div className="ri-view-toggle" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={view === 'schedule'}
            className={`ri-view-btn${view === 'schedule' ? ' is-active' : ''}`}
            onClick={() => overlay.setTab('schedule')}
          >
            Schedule
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === 'calendar'}
            className={`ri-view-btn${view === 'calendar' ? ' is-active' : ''}`}
            onClick={() => overlay.setTab('calendar')}
          >
            Calendar
          </button>
        </div>

        <label className="rm-search ri-search">
          <FiSearch />
          <input
            type="search"
            placeholder="Search applicants..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {view === 'schedule' ? (
        <div className="rm-table-wrap ri-table-scroll">
          <table className="rm-table">
            <thead>
              <tr>
                <th>Applicant</th>
                <th>Position</th>
                <th>Interviewer</th>
                <th>Type</th>
                <th>Date</th>
                <th>Time</th>
                <th>Location</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="9"
                    style={{ textAlign: 'center', padding: 28, color: '#6b7280' }}
                  >
                    Loading interviews…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan="9"
                    style={{ textAlign: 'center', padding: 28, color: '#6b7280' }}
                  >
                    No interviews yet. Click Schedule Interview to book one.
                  </td>
                </tr>
              ) : (
                filtered.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className="rm-applicant-cell">
                        <div
                          className="rm-avatar"
                          style={{ background: row.avatar }}
                        >
                          {applicantInitials(row.name)}
                        </div>
                        <p className="rm-applicant-name">{row.name}</p>
                      </div>
                    </td>
                    <td>{row.role}</td>
                    <td>{row.interviewer}</td>
                    <td>
                      <span className={`ri-type ri-type--${row.type}`}>
                        {INTERVIEW_TYPE_LABEL[row.type] || row.type}
                      </span>
                    </td>
                    <td>{row.date}</td>
                    <td>{row.time}</td>
                    <td>{row.location}</td>
                    <td>
                      <span className={`ri-status ri-status--${row.status}`}>
                        {INTERVIEW_STATUS_LABEL[row.status] || row.status}
                      </span>
                    </td>
                    <td>
                      <div className="rm-row-actions">
                        <button
                          type="button"
                          className="rm-icon-btn"
                          title="View"
                          onClick={() => openView(row)}
                        >
                          <FiEye />
                        </button>
                        <button
                          type="button"
                          className="rm-icon-btn"
                          title="Edit"
                          onClick={() => openEdit(row)}
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          className="rm-icon-btn"
                          title="Mark completed"
                          disabled={
                            updatingId === row.id || row.status === 'completed'
                          }
                          onClick={() => void markCompleted(row.id)}
                        >
                          <FiCheck />
                        </button>
                        <button
                          type="button"
                          className="rm-icon-btn"
                          title="Cancel"
                          disabled={
                            updatingId === row.id ||
                            row.status === 'cancelled' ||
                            row.status === 'completed'
                          }
                          onClick={() => void markCancelled(row.id)}
                        >
                          <FiX />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="ri-calendar">
          <div className="ri-cal-toolbar">
            <button
              type="button"
              className="ri-cal-nav"
              onClick={() =>
                setMonthAnchor(
                  new Date(
                    monthAnchor.getFullYear(),
                    monthAnchor.getMonth() - 1,
                    1,
                  ),
                )
              }
              aria-label="Previous month"
            >
              <FiChevronLeft />
            </button>
            <h3 className="ri-cal-month">{monthLabel}</h3>
            <button
              type="button"
              className="ri-cal-nav"
              onClick={() =>
                setMonthAnchor(
                  new Date(
                    monthAnchor.getFullYear(),
                    monthAnchor.getMonth() + 1,
                    1,
                  ),
                )
              }
              aria-label="Next month"
            >
              <FiChevronRight />
            </button>
            <button
              type="button"
              className="ri-cal-today"
              onClick={() => {
                const now = new Date();
                setMonthAnchor(startOfMonth(now));
                setSelectedDay(now);
              }}
            >
              Today
            </button>
          </div>

          <div className="ri-cal-grid" role="grid" aria-label="Interview calendar">
            {WEEKDAYS.map((d) => (
              <div key={d} className="ri-cal-weekday">
                {d}
              </div>
            ))}
            {monthCells.map((day, idx) => {
              if (!day) {
                return <div key={`empty-${idx}`} className="ri-cal-cell is-empty" />;
              }
              const key = dayKey(day);
              const items = interviewsByDayKey.get(key) || [];
              const isToday = sameDay(day, new Date());
              const isSelected = sameDay(day, selectedDay);
              return (
                <button
                  key={key}
                  type="button"
                  className={`ri-cal-cell${isToday ? ' is-today' : ''}${
                    isSelected ? ' is-selected' : ''
                  }${items.length ? ' has-events' : ''}`}
                  onClick={() => setSelectedDay(day)}
                >
                  <span className="ri-cal-daynum">{day.getDate()}</span>
                  <div className="ri-cal-events">
                    {items.slice(0, 3).map((ev) => (
                      <span
                        key={ev.id}
                        className={`ri-cal-pill ri-cal-pill--${ev.status}`}
                        title={`${ev.name} · ${ev.time}`}
                      >
                        {ev.time} {ev.name}
                      </span>
                    ))}
                    {items.length > 3 ? (
                      <span className="ri-cal-more">+{items.length - 3} more</span>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="ri-cal-detail">
            <div className="ri-cal-detail-head">
              <h4>
                {selectedDay.toLocaleDateString('en-AU', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </h4>
              <button
                type="button"
                className="rm-btn rm-btn--primary"
                onClick={() => openSchedule()}
              >
                <FiPlus /> Schedule
              </button>
            </div>
            {selectedDayInterviews.length === 0 ? (
              <p className="ri-cal-empty">No interviews on this day.</p>
            ) : (
              <ul className="ri-cal-list">
                {selectedDayInterviews.map((row) => (
                  <li key={row.id} className="ri-cal-item">
                    <div
                      className="rm-avatar"
                      style={{ background: row.avatar }}
                    >
                      {applicantInitials(row.name)}
                    </div>
                    <div className="ri-cal-item-body">
                      <strong>{row.name}</strong>
                      <span>
                        {row.time} · {row.role} ·{' '}
                        {INTERVIEW_TYPE_LABEL[row.type] || row.type}
                      </span>
                      <span>
                        {row.interviewer} · {row.location}
                      </span>
                    </div>
                    <span className={`ri-status ri-status--${row.status}`}>
                      {INTERVIEW_STATUS_LABEL[row.status] || row.status}
                    </span>
                    <div className="rm-row-actions">
                      <button
                        type="button"
                        className="rm-icon-btn"
                        title="View"
                        onClick={() => openView(row)}
                      >
                        <FiEye />
                      </button>
                      <button
                        type="button"
                        className="rm-icon-btn"
                        title="Edit"
                        onClick={() => openEdit(row)}
                      >
                        <FiEdit2 />
                      </button>
                      <button
                        type="button"
                        className="rm-icon-btn"
                        title="Mark completed"
                        disabled={
                          updatingId === row.id || row.status === 'completed'
                        }
                        onClick={() => void markCompleted(row.id)}
                      >
                        <FiCheck />
                      </button>
                      <button
                        type="button"
                        className="rm-icon-btn"
                        title="Cancel"
                        disabled={
                          updatingId === row.id ||
                          row.status === 'cancelled' ||
                          row.status === 'completed'
                        }
                        onClick={() => void markCancelled(row.id)}
                      >
                        <FiX />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {showSchedule ? (
        <ScheduleInterview
          preselectedApplicantId={preselectedApplicantId}
          onClose={() => overlay.closeOverlay()}
          onCreated={() => void fetchInterviews()}
        />
      ) : null}

      {overlay.viewing && activeInterview ? (
        <div className="aa-overlay" onClick={closeDetail}>
          <div className="aa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aa-header">
              <div>
                <h2>Interview details</h2>
                <p>
                  {activeInterview.name} · {activeInterview.role}
                </p>
              </div>
              <button type="button" className="aa-close" onClick={closeDetail}>
                ×
              </button>
            </div>
            <div className="aa-grid" style={{ marginBottom: 16 }}>
              <label className="aa-field">
                <span>Interviewer</span>
                <strong>{activeInterview.interviewer}</strong>
              </label>
              <label className="aa-field">
                <span>Type</span>
                <strong>
                  {INTERVIEW_TYPE_LABEL[activeInterview.type] ||
                    activeInterview.type}
                </strong>
              </label>
              <label className="aa-field">
                <span>Date</span>
                <strong>{activeInterview.date}</strong>
              </label>
              <label className="aa-field">
                <span>Time</span>
                <strong>{activeInterview.time}</strong>
              </label>
              <label className="aa-field">
                <span>Location</span>
                <strong>{activeInterview.location}</strong>
              </label>
              <label className="aa-field">
                <span>Status</span>
                <strong>
                  {INTERVIEW_STATUS_LABEL[activeInterview.status] ||
                    activeInterview.status}
                </strong>
              </label>
            </div>
            <label className="aa-field aa-field--full">
              <span>Notes</span>
              <strong>{activeInterview.notes || '—'}</strong>
            </label>
            <div className="aa-actions">
              <button
                type="button"
                className="aa-btn aa-btn--ghost"
                onClick={closeDetail}
              >
                Close
              </button>
              <button
                type="button"
                className="aa-btn aa-btn--primary"
                onClick={() => openEdit(activeInterview)}
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {overlay.editing && activeInterview ? (
        <div className="aa-overlay" onClick={closeDetail}>
          <div className="aa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="aa-header">
              <div>
                <h2>Edit interview</h2>
                <p>
                  {activeInterview.name} · {activeInterview.role}
                </p>
              </div>
              <button type="button" className="aa-close" onClick={closeDetail}>
                ×
              </button>
            </div>
            <form className="aa-form" onSubmit={saveEdit}>
              <div className="aa-grid">
                <label className="aa-field">
                  <span>Interviewer</span>
                  <input
                    value={editForm.interviewer}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        interviewer: e.target.value,
                      }))
                    }
                  />
                </label>
                <label className="aa-field">
                  <span>Type</span>
                  <select
                    value={editForm.type}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, type: e.target.value }))
                    }
                  >
                    <option value="video">Video Call</option>
                    <option value="in_person">In-Person</option>
                    <option value="phone">Phone</option>
                  </select>
                </label>
                <label className="aa-field">
                  <span>Date & time *</span>
                  <input
                    type="datetime-local"
                    value={editForm.scheduledAt}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        scheduledAt: e.target.value,
                      }))
                    }
                    required
                  />
                </label>
                <label className="aa-field">
                  <span>Location / link</span>
                  <input
                    value={editForm.location}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, location: e.target.value }))
                    }
                  />
                </label>
                <label className="aa-field">
                  <span>Status</span>
                  <select
                    value={editForm.status}
                    onChange={(e) =>
                      setEditForm((p) => ({ ...p, status: e.target.value }))
                    }
                  >
                    <option value="scheduled">Scheduled</option>
                    <option value="rescheduled">Rescheduled</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </label>
              </div>
              <label className="aa-field aa-field--full">
                <span>Notes</span>
                <textarea
                  rows={3}
                  value={editForm.notes}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, notes: e.target.value }))
                  }
                />
              </label>
              <div className="aa-actions">
                <button
                  type="button"
                  className="aa-btn aa-btn--ghost"
                  onClick={closeDetail}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="aa-btn aa-btn--primary"
                  disabled={savingEdit}
                >
                  {savingEdit ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
